import { SelectOwnProps } from ".";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { Box, styled, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";

export type SelectProps = SelectOwnProps &
  React.ComponentProps<typeof TextField> & {
    customLabelStyles?: boolean; // Add this prop to conditionally apply label styles
    labelShrink?: boolean; // Add this prop to control label shrink state
  };

const CssTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "icon" && prop !== "customize" && prop !== "customLabelStyles",
})<{
  icon?: boolean;
  customize?: boolean;
  customLabelStyles?: boolean; // Add this to styled component
}>(({ customize, icon, customLabelStyles }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "#FFFFFF",
    boxSizing: "border-box",
    borderRadius: "16px",
    border: "1px solid #ECEBF2",
    paddingLeft: icon ? "32px" : "7px",
    paddingRight: "32px",
    ...(customLabelStyles && {
      height: "65px",
    }),
    "&:hover": {
      backgroundColor: "#FFFFFF !important",
      borderColor: "#ECEBF2 !important",
    },
    "&.Mui-focused": {
      backgroundColor: "#FFFFFF !important",
      borderColor: "#ECEBF2 !important",
      boxShadow: "none !important",
    },
    "&.Mui-focused:hover": {
      backgroundColor: "#FFFFFF !important",
    },
  },
  "& .MuiFilledInput-root:after, .MuiFilledInput-root:before": {
    display: "none",
  },
  "& .MuiInputBase-root.MuiFilledInput-root.Mui-disabled": {
    backgroundColor: "#ececec",
    opacity: "0.5",
  },
  "& .MuiFilledInput-root .MuiSelect-select": {
    padding: customize ? "16px 32px 16px 16px" : "24px 32px 8px 12px",
    backgroundColor: "#FFFFFF !important",
    borderRadius: "16px",
    color: "#00000099 !important",
    "&:focus": {
      backgroundColor: "#FFFFFF !important",
    },
  },
  // Conditionally apply label styles only when customLabelStyles is true
  ...(customLabelStyles && {
    "& .MuiFormLabel-root": {
      left: icon && "0px",
      color: "#000000 !important",
      fontWeight: "700",
      fontSize: "15px",
      paddingLeft: "10px",
      marginTop: "5px",
    },
  }),
  "& .MuiFormHelperText-root": {
    marginLeft: icon ? "32px" : "14px",
    marginTop: "3px",
  },
  "& .MuiMenuItem-root": {
    "&:hover": {
      backgroundColor: "#e04343ff",
    },
    "&.Mui-selected": {
      backgroundColor: "transparent",
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
  "& .MuiMenuItem-root.Mui-selected": {
    backgroundColor: "#520A76 !important",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#6A1B9A !important",
    },
  },
}));

export default function Select({
  options = [],
  customize,
  customLabelStyles, // Add this prop
  labelShrink, // Add this prop
  StartIcon,
  StartIconProps,
  error,
  helperText,
  required,
  label,
  placeholder,
  onFocus,
  ...rest
}: SelectProps) {
  const [focused, setFocused] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    rest.onChange?.(event);
    (document.activeElement as HTMLElement | null)?.blur?.();
    setFocused(false);
  };

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);

  const hasIcon = Boolean(StartIcon);

  // Determine if label should shrink
  const shouldShrink = labelShrink !== undefined ? labelShrink : undefined;

  return (
    <>
      {hasIcon ? (
        <>
          <Box
            sx={{
              display: "flex",
              position: "relative",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                left: "16px",
                top: "16px",
                position: "absolute",
                zIndex: 1,
              }}
            >
              <StartIcon
                style={{
                  fontSize: "18px",
                  ...StartIconProps,
                }}
              />
            </Box>
            <CssTextField
              select
              fullWidth
              icon={true}
              customLabelStyles={customLabelStyles} // Pass the prop
              variant="filled"
              placeholder={placeholder}
              color="primary"
              error={error}
              label={label}
              required={required}
              InputLabelProps={{
                shrink: shouldShrink,
              }}
              SelectProps={{
                MenuProps: {
                  disableScrollLock: isMobile,
                  disableAutoFocus: true,
                  disableEnforceFocus: true,
                  disableRestoreFocus: true,
                  sx: {
                    maxHeight: 300,
                    "& .MuiPaper-root": {
                      borderRadius: "16px",
                    },
                  },
                },
              }}
              {...rest}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              {options.map(({ value, label }, index) => (
                <MenuItem key={index} value={value}>
                  {label}
                </MenuItem>
              ))}
            </CssTextField>
          </Box>
          {!focused && error && helperText && (
            <div
              style={{
                position: "absolute",
                marginTop: "4px",
                marginLeft: "14px",
                color: "rgb(183, 33, 54)",
                fontSize: "0.75rem",
                lineHeight: "1.66",
                fontWeight: "400",
              }}
            >
              {helperText}
            </div>
          )}
        </>
      ) : (
        <>
          {customize && (
            <Typography
              mb={1}
              fontSize={"12px"}
              fontWeight={400}
              textTransform={"uppercase"}
            >
              {label}
            </Typography>
          )}
          <CssTextField
            color="primary"
            select
            fullWidth
            icon={false}
            customize={customize}
            customLabelStyles={customLabelStyles} // Pass the prop here too
            placeholder={placeholder}
            variant="filled"
            label={!customize ? label : ""}
            error={error}
            required={required}
            InputLabelProps={{
              shrink: shouldShrink,
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected: any) => {
                if (!selected) {
                  return (
                    <span style={{ color: "#999", paddingLeft: 0 }}>
                      {placeholder}
                    </span>
                  );
                }
                const option = options.find((opt) => opt.value === selected);
                return option ? option.label : selected;
              },
              MenuProps: {
                disableScrollLock: isMobile,
                disableAutoFocus: true,
                disableEnforceFocus: true,
                disableRestoreFocus: true,
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    overflowY: "scroll",
                    borderRadius: "16px",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "transparent",
                      borderRadius: "16px",
                      margin: "13px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#aaa",
                      borderRadius: "16px",
                    },
                  },
                },
                sx: {
                  maxHeight: 300,
                  "& .MuiPaper-root": {
                    borderRadius: "16px",
                  },
                  "& .MuiMenuItem-root.Mui-selected": {
                    backgroundColor: "#F2F2F7 !important",
                    color: "black",
                  },
                },
              },
            }}
            {...rest}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <MenuItem disabled value="">
              {placeholder || "Select an option"}
            </MenuItem>
            {options.map(({ value, label }, index) => (
              <MenuItem key={index} value={value}>
                {label}
              </MenuItem>
            ))}
          </CssTextField>
          {!focused && error && helperText && (
            <div
              style={{
                position: "absolute",
                marginTop: "4px",
                marginLeft: "14px",
                color: "rgb(183, 33, 54)",
                fontSize: "0.75rem",
                lineHeight: "1.66",
                fontWeight: "400",
              }}
            >
              {helperText}
            </div>
          )}
        </>
      )}
    </>
  );
}
